import {
  Directive,
  Input,
  AfterViewInit,
  OnDestroy,
  NgZone
} from '@angular/core';
import { MultiSelect } from 'primeng/multiselect';
import { memoize } from 'lodash-es';

/** Type for original patched methods */
type PatchedMethod =
  | ((option: unknown) => boolean)
  | (() => boolean)
  | ((value: unknown) => void)
  | ((value: unknown[], event?: Event) => void);

/**
 * @deprecated Remove this directive when upgrading to PrimeNG 18+
 *
 * This directive patches PrimeNG 17's p-multiSelect selection methods
 * to use a Set for O(1) lookups instead of Array.some() which is O(n).
 *
 * PROBLEM:
 * PrimeNG 17's isSelected() uses: (modelValue || []).some(v => equals(v, optionValue))
 * For 18,000+ items, this creates O(n²) complexity causing the dropdown to freeze.
 *
 * SOLUTION:
 * Replace with Set.has() for O(1) lookups, reducing complexity from O(n²) to O(n).
 *
 * REMOVAL STEPS (when upgrading to PrimeNG 18):
 * 1. Search for 'optimizeSelection' in all templates
 * 2. Remove the attribute from p-multiSelect elements
 * 3. Delete this file
 * 4. Remove from module declarations
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'p-multiSelect[optimizeSelection]'
})
export class MultiselectOptimizeDirective implements AfterViewInit, OnDestroy {
  /**
   * Minimum number of options before applying optimization.
   * For small lists, the overhead of maintaining a Set isn't worth it.
   */
  @Input() optimizeThreshold = 100;

  /** Maximum time (ms) to wait for options to load before giving up */
  @Input() maxWaitTime = 3000;

  private selectionSet = new Set<string>();

  /** WeakMap cache for object-to-key mapping (allows GC of unused objects) */
  private objectKeyCache = new WeakMap<object, string>();

  /** Memoized key generator for primitive values */
  private primitiveKeyCache = memoize((value: string | number | boolean) =>
    String(value)
  );

  /** Map of patched method names to their original implementations */
  private patches = new Map<string, PatchedMethod>();

  private isPatched = false;
  private watchInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private multiSelect: MultiSelect,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    // Try immediately, then watch for options if not ready
    if (!this.tryApplyOptimization()) {
      this.watchForOptions();
    }
  }

  ngOnDestroy(): void {
    this.stopWatching();
    this.restoreAllPatches();
    this.primitiveKeyCache.cache.clear?.();
  }

  /**
   * Watches for options to be loaded using interval-based detection.
   * More reliable than fixed setTimeout delays for async-loaded data.
   */
  private watchForOptions(): void {
    const startTime = Date.now();

    this.ngZone.runOutsideAngular(() => {
      this.watchInterval = setInterval(() => {
        if (this.tryApplyOptimization()) {
          this.stopWatching();
          return;
        }

        // Stop watching after maxWaitTime
        if (Date.now() - startTime >= this.maxWaitTime) {
          this.stopWatching();
        }
      }, 100);
    });
  }

  private stopWatching(): void {
    if (this.watchInterval) {
      clearInterval(this.watchInterval);
      this.watchInterval = null;
    }
  }

  /**
   * Attempts to apply optimization if conditions are met.
   * @returns true if optimization was applied, false otherwise
   */
  private tryApplyOptimization(): boolean {
    if (this.isPatched) return true;

    const optionsCount = this.multiSelect.options?.length ?? 0;
    if (optionsCount < this.optimizeThreshold) {
      return false;
    }

    this.applyOptimization();
    return true;
  }

  private applyOptimization(): void {
    if (this.isPatched) return;

    // Build initial selection set
    this.rebuildSelectionSet();

    // Apply patches using declarative pattern
    this.applyPatch('isSelected', this.optimizedIsSelected.bind(this));
    this.applyPatch('allSelected', this.optimizedAllSelected.bind(this));
    this.applyPatch('writeValue', this.patchedWriteValue.bind(this));
    this.applyPatch('updateModel', this.patchedUpdateModel.bind(this));

    this.isPatched = true;
  }

  /**
   * Declarative patch application - stores original and applies replacement.
   */
  private applyPatch(
    methodName: keyof MultiSelect,
    replacement: PatchedMethod
  ): void {
    const original = this.multiSelect[methodName] as PatchedMethod | undefined;
    if (original && typeof original === 'function') {
      this.patches.set(
        methodName,
        original.bind(this.multiSelect) as PatchedMethod
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.multiSelect as any)[methodName] = replacement;
    }
  }

  /**
   * Restores all patched methods to their originals.
   */
  private restoreAllPatches(): void {
    if (!this.isPatched) return;

    for (const [methodName, original] of this.patches) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.multiSelect as any)[methodName] = original;
    }
    this.patches.clear();
    this.isPatched = false;
  }

  private optimizedIsSelected(option: unknown): boolean {
    try {
      const optionValue = this.multiSelect.getOptionValue(option);
      const key = this.getSelectionKey(optionValue);
      return this.selectionSet.has(key);
    } catch (error) {
      console.error(
        '[MultiselectOptimize] isSelected optimization failed, falling back to original:',
        error
      );
      const original = this.patches.get('isSelected');
      return original ? (original as (opt: unknown) => boolean)(option) : false;
    }
  }

  private optimizedAllSelected(): boolean {
    // If selectAll is explicitly set, use it
    if (
      this.multiSelect.selectAll !== null &&
      this.multiSelect.selectAll !== undefined
    ) {
      return this.multiSelect.selectAll;
    }

    const visibleOptions = this.multiSelect.visibleOptions?.() ?? [];
    if (!visibleOptions.length) return false;

    // Count valid selectable options and check if all are selected
    let validCount = 0;
    let selectedCount = 0;

    for (const option of visibleOptions) {
      // Skip groups
      if (this.multiSelect.isOptionGroup?.(option)) continue;
      // Skip disabled options
      if (this.multiSelect.isOptionDisabled?.(option)) continue;

      validCount++;
      if (this.optimizedIsSelected(option)) {
        selectedCount++;
      }
    }

    return validCount > 0 && selectedCount === validCount;
  }

  private patchedWriteValue(value: unknown): void {
    // Rebuild our set with the new value
    this.rebuildSelectionSetFromValue(value);

    // Call original writeValue
    const original = this.patches.get('writeValue');
    if (original) {
      (original as (val: unknown) => void)(value);
    }
  }

  private patchedUpdateModel(value: unknown[], event?: Event): void {
    // Update our set before calling original
    this.rebuildSelectionSetFromValue(value);

    // Call original updateModel
    const original = this.patches.get('updateModel');
    if (original) {
      (original as (val: unknown[], evt?: Event) => void)(value, event);
    }
  }

  private rebuildSelectionSet(): void {
    // Try to get model value from the signal
    let modelValue: unknown[] = [];
    try {
      modelValue = this.multiSelect.modelValue?.() ?? [];
    } catch {
      // If signal access fails, try the value property directly
      modelValue = this.multiSelect.value ?? [];
    }

    this.rebuildSelectionSetFromValue(modelValue);
  }

  private rebuildSelectionSetFromValue(value: unknown): void {
    this.selectionSet.clear();
    if (Array.isArray(value)) {
      for (const v of value) {
        this.selectionSet.add(this.getSelectionKey(v));
      }
    }
  }

  /**
   * Generates a string key for a value, using caching strategies:
   * - WeakMap for objects (allows GC)
   * - Memoized function for primitives
   */
  private getSelectionKey(value: unknown): string {
    if (value === null || value === undefined) {
      return '__null__';
    }

    if (typeof value === 'object') {
      // Use WeakMap cache for objects
      let key = this.objectKeyCache.get(value as object);
      if (!key) {
        key = JSON.stringify(value);
        this.objectKeyCache.set(value as object, key);
      }
      return key;
    }

    // Use memoized cache for primitives
    return this.primitiveKeyCache(value as string | number | boolean);
  }
}
